<script lang="ts" setup>
import { PropType, computed } from 'vue'

/* Assets */
import productNoImage from '@/assets/images/ProductNoImage.png'

/* Types */
import { Product } from '@/types/api/api-v3/product'
import { ConstructorPizza } from '@/types/api/api-v3/halves'

/* Utils */
import { formatToTitleOfProduct } from '@/utils/formatToTitleOfProduct'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  isSelectedRightHalve: {
    type: Boolean,
    required: true,
  },
  isSelectedLeftHalve: {
    type: Boolean,
    required: true,
  },
  constructorPizza: {
    type: Object as PropType<ConstructorPizza>,
    required: true,
    default: () => {
      return {
        leftHalve: null,
        rightHalve: null,
      }
    },
  },
})

const productImage = computed(() => props.product.image || productNoImage)

const isTitleProduct = computed(() => formatToTitleOfProduct(props.product))

const backgroundImage = computed(() => {
  if (!props.isSelectedRightHalve && !props.isSelectedLeftHalve)
    return `url(${productImage.value})`

  return `linear-gradient(to ${
    props.isSelectedLeftHalve ? 'left' : 'right'
  }, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 0%), url(${
    productImage.value
  })`
})

const emits = defineEmits(['selectHalvePizza'])

const selectHalvePizza = (product: Product) =>
  emits('selectHalvePizza', product)

const checkSelectedLeftHalve = (product: Product) => {
  return props.constructorPizza?.leftHalve
    ? props.constructorPizza.leftHalve.id === product.id
    : false
}

const checkSelectedRightHalve = (product: Product) => {
  return props.constructorPizza?.rightHalve
    ? props.constructorPizza.rightHalve.id === product.id
    : false
}
</script>

<template>
  <div
    class="product"
    :class="{
      'product--selected':
        checkSelectedLeftHalve(product) || checkSelectedRightHalve(product),
    }"
  >
    <div
      class="product__image"
      :style="{
        'background-image': `${backgroundImage}`,
      }"
      @click="selectHalvePizza(product)"
    />
    <div v-if="product.name" class="product__title">
      {{ isTitleProduct }}
    </div>
    <div class="product__wrapper">
      <div v-if="product.description" class="product__compound">
        Состав
        <v-tooltip
          activator="parent"
          attach="#__nuxt"
          color="#000000"
          :location="'right'"
        >
          <span class="product__ingredients">
            {{ product.description }}
          </span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.product {
  display: flex;
  flex-direction: column;
  max-width: 120px;
  height: 100%;

  &__image {
    width: 120px;
    height: 120px;
    background-position: center;
    background-origin: border-box;
    background-size: 120px 80px;

    @include media('<=tablet') {
      width: 100%;
      height: 95px;
      background-size: 95px 63px;
    }
  }

  &--selected {
    .product__image {
      position: relative;
      border: 3px solid $success !important;
      border-radius: 20px;

      &::after {
        position: absolute;
        top: -15px;
        right: -15px;
        display: block;
        width: 32px;
        height: 32px;
        content: '';
        background: url('./assets/images/success-icon.svg');
        background-size: contain;
      }
    }
  }

  &__wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: auto;
  }

  &__title {
    margin-top: 10px;
    font-size: 14px;
    font-weight: 700;
    line-height: 17px;
  }

  &__compound {
    font-size: 12px;
    font-weight: 700;
    line-height: 28px;
    color: #9ea7b6;
    cursor: pointer;

    &-name {
      width: fit-content;
      font-size: 12px;
      font-weight: 700;
      line-height: 28px;
      color: $unactive-grey;
      cursor: pointer;
    }
  }
}

:deep(.v-overlay__content) {
  width: initial;
  max-width: 190px !important;
  padding: 20px 16px !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 20px !important;
  color: #fff;
  background-color: #000 !important;
  border-radius: 15px !important;
  opacity: 0.9 !important;
}
</style>
